import React, { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import ValidationLayout from "../components/AdminValidations";
import BandwidthMarketplace from "../components/BandwidthMarketplace";
import { SortOrder } from "../enums";
import bandwidthMarketplaceService from "../services/bandwidthMarketplace.service";
import { TableConstants, YearDaysForCalculation, getPortChargePerMonth, priceFilterDropdown, defaultPortName } from "../utils/appConstants";
import PurchaseDrawer from "../components/BandwidthMarketplace/PurchaseBandwidth";
import {
  SuccessMessage,
  ErrorCodeMessageMapping,
  ErrorCodesMapping,
  MY_SELLER_BUCKET_PRE_URL,
  TERMS_DEFAULT_FILE,
  ErrorMessages as Errors,
} from "../utils/apiConstants";
import PdfViewer from "../components/PdfViewer";
import SuccessInfoIcon from "../assets/images/success-toast-checkmark-icon.svg";
import ErrorInfoIcon from "../assets/images/error-info-icon.svg";
import SwipeIcon from "../assets/images/swipe-icon.svg";
import { sumCount } from "../utils/methods";
import AddPortService from "../services/addPort.service";
import Dropdown from "../components/DesignSystem/dropdown_autocomplete";
import DropdownCheckbox from "../components/DesignSystem/dropdown_checkbox";
import Input from "../components/DesignSystem/input";
import Button from "../components/DesignSystem/button";
import SearchIcon from "../assets/images/search-icon.svg";

const BandwidthMarketplacePage: React.FC = () => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const [prevSearchText, setPrevSearchText] = useState<string>("");
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState<string>("service_description");
  const [page, setPage] = useState<number>(1);
  const [searchTextNeElements, setSearchTextNeElements] = useState<string>("");
  const [searchTextFeElements, setSearchTextFeElements] = useState<string>("");
  const [bandwidthPorts, setBandwidthPorts] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [purchaseOpen, setPurchaseOpen] = useState<boolean>(false);
  const [selectedRowForPurchase, setSelectedRowForPurchase] = useState<any>();
  const [total, setTotal] = useState<number>(0);
  const [verified, setVerified] = useState<boolean>(false);
  const [priceFilterValue, setPriceFilterValue] = useState<string>(priceFilterDropdown[0].value);
  const [values, setValues] = useState<any>({});
  // const [nePortName, setNePortName] = useState<string>('');
  // const [neServiceDescription, setNeServiceDescription] = useState<string>('');
  // const [fePortName, setFePortName] = useState<string>('');
  // const [feServiceDescription, setFeServiceDescription] = useState<string>('');
  const isComponentMounted = React.useRef(true);
  React.useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  const [onContractSelected, setOnContractSelected] = useState<boolean>(false);
  const [aggrementTypeInfo, setAggrementTypeInfo] = useState<{
    type: string;
    url: string;
  }>({
    type: "",
    url: "",
  });
  const [agreementSelected, setAgreementSelected] = useState<{
    on_contract: boolean;
    on_demand: boolean;
  }>({
    on_contract: false,
    on_demand: false,
  });
  const [selectedPrice, setSelectedPrice] = useState<string>("");

  const [onDemandSelected, setOnDemandSelected] = useState<boolean>(false);
  const [onContractPrice, setOnContractPrice] = useState<string>("");
  const [onDemandPrice, setOnDemandPrice] = useState<string>("");

  const fetchBandwidthForMarketPlace = useCallback(() => {
    setLoading(true);
    const search = location && location.state && location.state.length > 0 ? location.state.join(" -|- ") : "";
    let searchTextToUse = "";
    if (search) {
      searchTextToUse = search;
    } else {
      searchTextToUse = searchText;
    }
    const payload = {
      sortField: orderBy,
      sortDirection: order.toUpperCase(),
      page,
      pageSize: TableConstants.perPage,
      searchNneName: searchTextNeElements,
      searchFneName: searchTextFeElements,
      filter: priceFilterValue,
      searchText: searchTextToUse,
    };

    bandwidthMarketplaceService
      .getAllbandwidthMarketplace(payload)
      .then((response: any) => {
        if (!isComponentMounted.current) return;
        setLoading(false);
        setBandwidthPorts(response?.data.records ?? []);
        setTotal(response?.pagination?.totalRecords ?? 0);
        setNetworkElements(response?.data.networkElements ?? []);
        setPrevSearchText(searchTextToUse);
        navigate(location.pathname, { replace: true });
      })
      .catch(() => {
        if (!isComponentMounted.current) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setLoading(false);
        setBandwidthPorts([]);
        setTotal(0);
        navigate(location.pathname, { replace: true });
      });
  }, [location, setLoading, setBandwidthPorts, setTotal, priceFilterValue, orderBy, order, page, searchTextNeElements, searchTextFeElements, searchText]);

  const setInitVals = useCallback(
    (port: any) => {
      setValues({
        nePortName: "",
        neServiceDescription: `> ${port.far_network_element}`,
        fePortName: "",
        feServiceDescription: `> ${port.near_network_element}`,
        bandwidthDescription: `${port.organization_name} ${port.service_description}`,
      });
    },
    [setValues, values]
  );

  const handlePurchaseOpen = useCallback(
    (port: any) => {
      setInitVals(port);
      setOnContractPrice((port.on_contract_price + getPortChargePerMonth(port) * 2).toString());
      setOnDemandPrice(port.on_demand_price);
      setSelectedRowForPurchase(port);
      setPurchaseOpen(true);
    },
    [setPurchaseOpen, setOnContractPrice, setOnDemandPrice, setSelectedRowForPurchase]
  );

  const handleRequestSort = useCallback(
    (property: string, nextSortOrder: SortOrder) => {
      setOrder(nextSortOrder);
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleClosePurchaseBandwidth = useCallback(() => {
    setValues({});
    setPurchaseOpen(false);
    setOnContractSelected(false);
    setSelectedPrice("");
    setAgreementSelected({
      on_contract: false,
      on_demand: false,
    });
    setAggrementTypeInfo({
      type: "",
      url: "",
    });
    setOnDemandSelected(false);
    setOnDemandPrice("");
    setOnContractPrice("");
  }, [
    setValues,
    values,
    setSelectedPrice,
    setPurchaseOpen,
    setAggrementTypeInfo,
    setOnContractSelected,
    setAgreementSelected,
    setOnDemandSelected,
    setOnDemandPrice,
    setOnContractPrice,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]: value,
      });
    },
    [setValues, values]
  );

  const handlePurchaseClick = useCallback(() => {
    setLoading(true);
    const onDemandPerMonth = onDemandPrice
      ? ((parseFloat(onDemandPrice) / 100) * ((60 * 24 * YearDaysForCalculation) / 12) + getPortChargePerMonth(selectedRowForPurchase) * 2).toFixed(2)
      : 0;
    const onDemandPerMinute = onDemandPerMonth ? (parseFloat(onDemandPerMonth) * 100 * 12) / (YearDaysForCalculation * 24 * 60) : 0;
    let price = selectedPrice === "on_demand" ? onDemandPerMinute : onContractPrice;
    price = Number(price).toFixed(2);
    const payload: any = {
      bandwidthDetailId: selectedRowForPurchase?.id,
      nneId: selectedRowForPurchase?.nne_id,
      // nearPortName: values.nePortName || defaultPortName,
      // nearPortDescription: values.neServiceDescription,
      fneId: selectedRowForPurchase?.fne_id,
      // farPortName: values.nePortName || defaultPortName,
      // farPortDescription: values.feServiceDescription,
      price,
      priceType: selectedPrice,
      // serviceDescription: values.bandwidthDescription,
    };
    bandwidthMarketplaceService
      .purchaseBandwidth(payload)
      .then(() => {
        if (!isComponentMounted.current) return;
        setLoading(false);
        handleClosePurchaseBandwidth();
        const message = SuccessMessage.purchaseBandwidthSuccess;
        toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        if (bandwidthPorts.length > 1) {
          fetchBandwidthForMarketPlace();
        } else if (page !== 1) {
          setPage(1);
        } else {
          fetchBandwidthForMarketPlace();
        }
      })
      .catch((e: any) => {
        if (!isComponentMounted.current) return;
        setLoading(false);
        if (e.errorCode === ErrorCodesMapping[1007]) {
          toast.error(ErrorCodeMessageMapping[1007], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
      });
  }, [
    values,
    selectedPrice,
    onDemandSelected,
    onContractSelected,
    agreementSelected,
    onContractPrice,
    onDemandPrice,
    selectedRowForPurchase?.bandwidth_detail_id,
    setLoading,
    handleClosePurchaseBandwidth,
    fetchBandwidthForMarketPlace,
  ]);

  const [neInput, setNeInput] = useState<string>("");
  const [feInput, setFeInput] = useState<string>("");
  const handleNeInputChange = useCallback(
    (input: string) => {
      setNeInput(input);
    },
    [setNeInput]
  );
  const handleNeValueChange = useCallback(
    (value: string) => {
      setSearchTextNeElements(value);
    },
    [setSearchTextNeElements]
  );
  const handleFeValueChange = useCallback(
    (value: string) => {
      setSearchTextFeElements(value);
    },
    [setSearchTextFeElements]
  );
  const handleFeInputChange = useCallback(
    (input: string) => {
      setFeInput(input);
    },
    [setFeInput]
  );
  const handleRequestSearch = useCallback(
    (e: any) => {
      if (e.target.name === "nne_name") {
        setPage(1);
        setSearchTextNeElements(e.target.value);
      } else {
        setPage(1);
        setSearchTextFeElements(e.target.value);
      }
    },
    [setSearchTextNeElements, setSearchTextFeElements]
  );

  const handleSelectedPriceChange = useCallback(
    (selectedPrice: string) => {
      setSelectedPrice((prev: string) => {
        if (prev === selectedPrice) {
          return "";
        }
        return selectedPrice;
      });
      setAgreementSelected((prev) => {
        return {
          on_demand: false,
          on_contract: false,
        };
      });
    },
    [setSelectedPrice, setAgreementSelected]
  );

  const handleSearchClose = useCallback(
    (id: any) => {
      if (id === "nne_name") {
        setSearchTextNeElements("");
      } else {
        setSearchTextFeElements("");
      }
    },
    [setSearchTextNeElements, setSearchTextFeElements]
  );

  const handlePriceFilter = useCallback(
    (value: any) => {
      const object = value.target.value[0] === priceFilterDropdown[1].text ? priceFilterDropdown[2].text : priceFilterDropdown[1].text;
      setPage(1);
      const priceValueArray = [];
      if (priceFilterValue === priceFilterDropdown[0].value) {
        priceValueArray.push(priceFilterDropdown[1].text);
        priceValueArray.push(priceFilterDropdown[2].text);
      }
      if (priceFilterValue === priceFilterDropdown[1].value) {
        priceValueArray.push(priceFilterDropdown[1].text);
      }
      if (priceFilterValue === priceFilterDropdown[2].value) {
        priceValueArray.push(priceFilterDropdown[2].text);
      }
      const index = priceValueArray.findIndex((item) => item == object);
      if (index !== -1) {
        priceValueArray.splice(index, 1);
      } else {
        priceValueArray.push(object);
      }
      if (priceValueArray.includes(priceFilterDropdown[1].text) && !priceValueArray.includes(priceFilterDropdown[2].text)) {
        setPriceFilterValue(priceFilterDropdown[1].value);
      } else if (priceValueArray.includes(priceFilterDropdown[2].text) && !priceValueArray.includes(priceFilterDropdown[1].text)) {
        setPriceFilterValue(priceFilterDropdown[2].value);
      } else setPriceFilterValue(priceFilterDropdown[0].value);
    },
    [priceFilterValue, setPriceFilterValue, setPage]
  );

  const handleTermsOpenClick = useCallback(
    (type: string) => {
      setPdfViewerOpen(true);

      const fileName = type === "on_contract" ? selectedRowForPurchase.terms_on_contract_url : selectedRowForPurchase.terms_on_demand_url;
      setAggrementTypeInfo((prev) => {
        return {
          type,
          url: fileName ? MY_SELLER_BUCKET_PRE_URL + fileName : MY_SELLER_BUCKET_PRE_URL + TERMS_DEFAULT_FILE,
        };
      });
    },
    [setPdfViewerOpen, setAggrementTypeInfo, selectedRowForPurchase]
  );

  const handleTermsCloseClick = useCallback(
    (agrred = false, agreementType = "") => {
      // here
      setPdfViewerOpen(false);
      agreementType &&
        setAgreementSelected((prev) => {
          return {
            ...prev,
            [agreementType]: agrred,
          };
        });
      if (agreementType && (agreementType === "on_contract" || agreementType === "on_demand")) {
        setSelectedPrice(agreementType);
      }
    },
    [setPdfViewerOpen, setAgreementSelected, setSelectedPrice]
  );

  const handleSearchTextChanged = useCallback(
    (e: any) => {
      setSearchText(e.target.value);
      setPage(1);
      if (!e.target.value) {
        fetchBandwidthForMarketPlace();
        setSearchText("");
      }
    },
    [setSearchText, setPage, fetchBandwidthForMarketPlace]
  );
  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter") fetchBandwidthForMarketPlace();
    },
    [fetchBandwidthForMarketPlace]
  );

  const [networkElements, setNetworkElements] = useState<any>([]);

  useEffect(() => {
    fetchBandwidthForMarketPlace();
  }, [page, orderBy, verified, order, searchTextNeElements, searchTextFeElements]);

  const priceValueArray = [];
  if (priceFilterValue === priceFilterDropdown[0].value) {
    priceValueArray.push(priceFilterDropdown[1].text);
    priceValueArray.push(priceFilterDropdown[2].text);
  }
  if (priceFilterValue === priceFilterDropdown[1].value) {
    priceValueArray.push(priceFilterDropdown[1].text);
  }
  if (priceFilterValue === priceFilterDropdown[2].value) {
    priceValueArray.push(priceFilterDropdown[2].text);
  }

  const priceDropdown = (
    <div className="price-filter-dropdown">
      <DropdownCheckbox
        width={200}
        title="Purchase Type"
        value={priceValueArray}
        options={priceFilterDropdown.map((item: any, index: any, array: any) => item.text).filter((item: any) => item !== priceFilterDropdown[0].text)}
        customDisplayMethod={() => {
          if (priceFilterValue === priceFilterDropdown[0].value) {
            return priceFilterDropdown[0].text;
          }
          if (priceFilterValue === priceFilterDropdown[1].value) {
            return priceFilterDropdown[1].text;
          }
          if (priceFilterValue === priceFilterDropdown[2].value) {
            return priceFilterDropdown[2].text;
          }
          return "";
        }}
        onChange={handlePriceFilter}
      />
    </div>
  );

  return (
    <>
      {/* <ValidationLayout
        title="Buy Links from Marketplace"
        table={(
          <BandwidthMarketplace
            page={page}
            selected={selected}
            handlePurchaseOpen={handlePurchaseOpen}
            total={total}
            bandwidthPorts={bandwidthPorts}
            loading={loading}
            order={order}
            orderBy={orderBy}
            onPageChange={handlePageChange}
            onRequestSort={handleRequestSort}
            onRequestSearch={handleRequestSearch}
            onSearchClose={handleSearchClose}
          />
          )}
        onPriceFilterChange={handlePriceFilter}
        priceFilterValue={priceFilterValue}
        isPriceFilter
        dropdown={(
          <div className="dropdown-filters">
            <Dropdown
              title="Near End Node Filter"
              className="bandwidth filter"
              inputValue={neInput}
              onInputChange={handleNeInputChange}
              value={searchTextNeElements}
              onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
              options={networkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextFeElements)}
              onChange={handleNeValueChange}
            />
            <img src={SwipeIcon} alt="connector" />
            <Dropdown
              title="Far End Node Filter"
              className="bandwidth filter"
              inputValue={feInput}
              onInputChange={handleFeInputChange}
              value={searchTextFeElements}
              onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
              options={networkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextNeElements)}
              onChange={handleFeValueChange}
            />
          </div>
        )}
        onSearchChanges={handleSearchTextChanged}
        bandwidth
        isMarketplace
        isSearchSection
      /> */}
      <div className="verification-root manage-link-view" data-testid="admin-verification">
        <div className="sticky-top">
          <div className="title-main">
            <div className="title-root" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
              <h3>Buy Links from Marketplace</h3>
              {/* <div className="group-ungroup-div table-filters">{priceDropdown}</div> */}
            </div>
          </div>
          <div className="title-sub">
            <div className="flex">
              <div className="dropdown-filters">
                <Dropdown
                  title="Select Near End"
                  className="bandwidth filter"
                  inputValue={neInput}
                  onInputChange={handleNeInputChange}
                  value={searchTextNeElements}
                  onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                  options={networkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextFeElements)}
                  onChange={handleNeValueChange}
                />
                <img src={SwipeIcon} alt="connector" />
                <Dropdown
                  title="Select Far End"
                  className="bandwidth filter"
                  inputValue={feInput}
                  onInputChange={handleFeInputChange}
                  value={searchTextFeElements}
                  onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                  options={networkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextNeElements)}
                  onChange={handleFeValueChange}
                />
              </div>
              <div className="search-box" style={{ width: "100%" }}>
                <div className="group-ungroup-div table-filters">
                  <Input
                    variant="input-pre-icon"
                    name="user-search"
                    placeholder="Search"
                    icon={SearchIcon}
                    onChange={handleSearchTextChanged}
                    onKeypress={handleKeypress}
                    onBlur={() => {
                      if (searchText !== prevSearchText) fetchBandwidthForMarketPlace();
                    }}
                  />
                  {/* <div className="search-button-div" style={{ paddingLeft: "3px" }}>
                    <Button
                      variant="search"
                      node="Search"
                      className="search-btn"
                      disabled={searchText === prevSearchText}
                      onClick={() => fetchBandwidthForMarketPlace()}
                    />
                  </div> */}
                </div>
              </div>
            </div>
            <div />
          </div>
        </div>
        <div className="sticky-bottom">
          <BandwidthMarketplace
            page={page}
            selected={selected}
            handlePurchaseOpen={handlePurchaseOpen}
            total={total}
            bandwidthPorts={bandwidthPorts}
            loading={loading}
            order={order}
            orderBy={orderBy}
            onPageChange={handlePageChange}
            onRequestSort={handleRequestSort}
            onRequestSearch={handleRequestSearch}
            onSearchClose={handleSearchClose}
          />
        </div>
      </div>
      <PurchaseDrawer
        loading={loading}
        selectedRowForPurchase={selectedRowForPurchase}
        open={purchaseOpen}
        onClose={handleClosePurchaseBandwidth}
        agreementSelected={agreementSelected}
        onContractSelected={onContractSelected}
        onDemandSelected={onDemandSelected}
        onContractPrice={onContractPrice}
        onDemandPrice={onDemandPrice}
        values={values}
        selectedPrice={selectedPrice}
        onSelectedPriceChange={handleSelectedPriceChange}
        onAggredTermsClick={handleTermsOpenClick}
        handleInputChange={handleInputChange}
        handlePurchaseClick={handlePurchaseClick}
        onAcceptTermsBypass={handleTermsCloseClick}
      />
      <PdfViewer open={pdfViewerOpen} onClose={handleTermsCloseClick} aggrementType={aggrementTypeInfo.type} url={aggrementTypeInfo.url} />
    </>
  );
};

export default BandwidthMarketplacePage;
